/**
 * components/forces/ForceArrow.tsx — Freccia di un carico applicato (MUTA,
 * 28-09-2026, richiesta utente).
 *
 * Riceve un `ForceArrowSpec` GIA' risolto (due punti mondo + etichetta) e
 * disegna: asta (linea da `from` a `to`), punta (`Arrowhead`, riusato dalle
 * quote con dimensioni proprie da `FORCE_ARROW_DEFAULTS`) ed etichetta
 * (`QuoteLabel`, riusato cosi' com'e' — stesso wrapper billboard, colori
 * diversi via prop). Nessun calcolo di geometria qui (SPEC §6, AC-7).*/

import { Line } from "@react-three/drei";
import { useMemo } from "react";
import * as THREE from "three";
import { COLORS } from "../../config/colors";
import { FORCE_ARROW_DEFAULTS } from "../../config/defaults";
import type { Vec3 } from "../../data/types";
import type { ForceArrowSpec } from "../../model/forces";
import { Arrowhead } from "../quotes/Arrowhead";
import { QuoteLabel } from "../quotes/QuoteLabel";

export interface ForceArrowProps {
  force: ForceArrowSpec;
}

/** Render di una freccia di carico dallo `ForceArrowSpec` risolto.*/
export function ForceArrow({ force }: ForceArrowProps) {
  const points = useMemo<[THREE.Vector3, THREE.Vector3]>(
    () => [new THREE.Vector3(...force.from), new THREE.Vector3(...force.to)],
    [force.from, force.to],
  );
  const direction = useMemo<Vec3>(
    () => [force.to[0] - force.from[0], force.to[1] - force.from[1], force.to[2] - force.from[2]],
    [force.from, force.to],
  );

  return (
    <group>
      <Line points={points} color={COLORS.forceArrow} />
      <Arrowhead
        tip={force.to}
        direction={direction}
        color={COLORS.forceArrow}
        lengthMm={FORCE_ARROW_DEFAULTS.headLengthMm}
        widthMm={FORCE_ARROW_DEFAULTS.headWidthMm}
      />
      <QuoteLabel
        position={force.labelPosition}
        text={force.label}
        textColor={COLORS.forceLabelText}
        backgroundColor={COLORS.forceLabelBackground}
        sizeMm={FORCE_ARROW_DEFAULTS.labelSizeMm}
      />
    </group>
  );
}
